import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { supabase, clearStaleAuth } from '@/lib/supabase';

const ADMIN_STORAGE_KEY = 'jb_admin_session_v2';

interface AdminContextValue {
  isAdmin: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signupFirstAdmin: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
}

const AdminContext = createContext<AdminContextValue>({
  isAdmin: false,
  isLoading: true,
  login: async () => ({ success: false }),
  signupFirstAdmin: async () => ({ success: false }),
  logout: async () => {},
});

export function AdminProvider({ children }: { children: ReactNode }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check admin status on mount and auth changes
  const checkAdminStatus = useCallback(async () => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      // If refresh token is invalid, clear everything
      if (sessionError && sessionError.message?.toLowerCase().includes('refresh token')) {
        await clearStaleAuth();
        localStorage.removeItem(ADMIN_STORAGE_KEY);
        setIsAdmin(false);
        setIsLoading(false);
        return;
      }

      if (!session?.user?.email) {
        setIsAdmin(false);
        localStorage.removeItem(ADMIN_STORAGE_KEY);
        setIsLoading(false);
        return;
      }

      // Check if this user's email is in admin_access table
      const { data: adminRecord } = await supabase
        .from('admin_access')
        .select('id')
        .eq('email', session.user.email)
        .maybeSingle();

      if (adminRecord) {
        setIsAdmin(true);
        localStorage.setItem(ADMIN_STORAGE_KEY, '1');
      } else {
        setIsAdmin(false);
        localStorage.removeItem(ADMIN_STORAGE_KEY);
      }
    } catch (err) {
      // If any auth error occurs, clear state
      const message = err instanceof Error ? err.message : '';
      if (message.toLowerCase().includes('refresh token') || message.toLowerCase().includes('invalid')) {
        await clearStaleAuth();
        localStorage.removeItem(ADMIN_STORAGE_KEY);
      }
      setIsAdmin(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Also check localStorage for fast initial render
    try {
      const stored = localStorage.getItem(ADMIN_STORAGE_KEY) === '1';
      if (stored) setIsAdmin(true);
    } catch { /* ignore */ }

    checkAdminStatus();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        setIsAdmin(false);
        localStorage.removeItem(ADMIN_STORAGE_KEY);
      }
      checkAdminStatus();
    });

    return () => subscription.unsubscribe();
  }, [checkAdminStatus]);

  const login = useCallback(async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      // Clear any stale tokens before attempting login
      await clearStaleAuth();

      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) return { success: false, error: error.message };
      if (!data.user?.email) return { success: false, error: 'Erro ao obter dados do usuário' };

      // Check admin_access table
      const { data: adminRecord } = await supabase
        .from('admin_access')
        .select('id')
        .eq('email', data.user.email)
        .maybeSingle();

      if (adminRecord) {
        setIsAdmin(true);
        localStorage.setItem(ADMIN_STORAGE_KEY, '1');
        return { success: true };
      }

      // Not an admin — sign out
      await supabase.auth.signOut();
      return { success: false, error: 'Este usuário não tem permissão de administrador.' };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao fazer login';
      // Clear on refresh token errors during login too
      if (message.toLowerCase().includes('refresh token')) {
        await clearStaleAuth();
      }
      return { success: false, error: message };
    }
  }, []);

  const signupFirstAdmin = useCallback(async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      // Check if any admin already exists
      const { data: existingAdmins, error: countError } = await supabase
        .from('admin_access')
        .select('id', { count: 'exact', head: true });

      if (countError) return { success: false, error: countError.message };

      // If we got a count and it's > 0, first access is already taken
      const hasAdmins = (existingAdmins as unknown as number) > 0 || false;
      if (hasAdmins) {
        return { success: false, error: 'Já existe um administrador cadastrado. Use a tela de login.' };
      }

      // Clear stale tokens before sign up
      await clearStaleAuth();

      // Sign up the user
      const { data, error: signUpError } = await supabase.auth.signUp({ email, password });
      if (signUpError) return { success: false, error: signUpError.message };
      if (!data.user?.email) return { success: false, error: 'Erro ao criar conta' };

      // Insert into admin_access
      const { error: insertError } = await supabase
        .from('admin_access')
        .insert({ email: data.user.email });

      if (insertError) {
        return { success: false, error: insertError.message };
      }

      setIsAdmin(true);
      localStorage.setItem(ADMIN_STORAGE_KEY, '1');
      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao criar conta';
      return { success: false, error: message };
    }
  }, []);

  const logout = useCallback(async () => {
    await clearStaleAuth();
    localStorage.removeItem(ADMIN_STORAGE_KEY);
    setIsAdmin(false);
  }, []);

  return (
    <AdminContext.Provider value={{ isAdmin, isLoading, login, signupFirstAdmin, logout }}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin(): AdminContextValue {
  return useContext(AdminContext);
}